import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideZoneChangeDetection } from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { PreloadAllModules, provideRouter, withPreloading, withRouterConfig } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { MSAL_INSTANCE, MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { BrowserCacheLocation, IPublicClientApplication, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { APP_CONFIGURATION, AppConfiguration } from "@ramudden/core/constants";
import { ConfigurationService } from "@ramudden/services";
import { provideToastr } from "ngx-toastr";
import { jsonInterceptor } from "@ramudden/interceptors";
import { provideUi } from "@ramudden/ui";
import { environment } from "../environments/environment";
import { AppComponent } from "./app.component";
import { routes } from "./app.routes";
import { ToastComponent } from "./components/shared/toast/toast.component";
import { SigncoErrorHandler } from "./error-handler";
import { authInterceptor } from "./interceptors/auth.interceptor";
import { conversationIdInterceptor } from "./interceptors/conversation-id.interceptor";
import { defaultInterceptor } from "./interceptors/default.interceptor";
import { AuthenticationService } from "./services/authentication.service";
import { SigncoUntypedFormBuilder } from "./services/signco-untyped-form-builder.service";

function initializeConfiguration(): () => Promise<any> {
    const configurationService = inject(ConfigurationService);
    return () => AppComponent.initializeConfiguration(configurationService);
}

function initializeAuthentication(): () => Promise<any> {
    const authenticationService = inject(AuthenticationService);
    return () => AppComponent.initializeAuthentication(authenticationService);
}

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/configuration.md
 */
function msalConfigFactory(configurationService: ConfigurationService): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: configurationService.configuration.azureAuthenticationClientId,
            authority: configurationService.configuration.azureAuthenticationAuthority,
            redirectUri: window.location.origin,
            knownAuthorities: [configurationService.configuration.azureAuthenticationAuthority],
            postLogoutRedirectUri: window.location.origin,
        },
        cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
        system: {
            /**
             * Below you can configure MSAL.js logs. For more information, visit:
             * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
             */
            loggerOptions: {
                loggerCallback(logLevel: LogLevel, message: string) {
                    console.log(message);
                },
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
            },
        },
    });
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideRouter(routes, withPreloading(PreloadAllModules), withRouterConfig({ onSameUrlNavigation: "reload" })),
        provideHttpClient(withInterceptors([authInterceptor, conversationIdInterceptor, defaultInterceptor, jsonInterceptor])),
        provideTranslateService({
            loader: provideTranslateHttpLoader(),
        }),
        provideAnimationsAsync(),
        provideUi(),
        provideToastr({
            toastComponent: ToastComponent,
        }),
        provideServiceWorker("ngsw-worker.js", {
            enabled: !location.hostname.includes("localhost"),
            registrationStrategy: "registerWhenStable:30000",
        }),
        {
            provide: APP_CONFIGURATION,
            useValue: { svgVersion: environment.svgVersion, appName: "portal" } as AppConfiguration,
        },
        provideAppInitializer(() => {
            const initializerFn = initializeConfiguration();
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initializeAuthentication();
            return initializerFn();
        }),
        { provide: ErrorHandler, useClass: SigncoErrorHandler },
        /**
         * Here we pass the configuration parameters to create an MSAL instance.
         * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/configuration.md
         */
        { provide: MSAL_INSTANCE, useFactory: msalConfigFactory, deps: [ConfigurationService], multi: false },
        MsalService,
        MsalBroadcastService,
        { provide: UntypedFormBuilder, useClass: SigncoUntypedFormBuilder },
    ],
};
